import { useEffect, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetListOpenMarketQuery, useSubscribeToOpenMarketMutation } from '@/store/api';
import { toast } from '@/components/ui/use-toast';

interface UseOpenMarketNotificationsProps {
	eventId: string | undefined;
	lobbyResponse: any;
	isCrowdLockMode: boolean;
	isRegisteredUser: boolean;
	slatesData: any[];
}

export function useOpenMarketNotifications({
	eventId,
	lobbyResponse,
	isCrowdLockMode,
	isRegisteredUser,
	slatesData,
}: UseOpenMarketNotificationsProps) {
	const { data: notificationsListOpenMarket = [], isError } = useGetListOpenMarketQuery(
		eventId && lobbyResponse && !isCrowdLockMode && isRegisteredUser ? eventId : skipToken
	);

	const [subscribeToOpenMarket] = useSubscribeToOpenMarketMutation();

	useEffect(() => {
		if (isError) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Could not load open market notifications.',
			});
		}
	}, [isError]);

	const matchOpeningDetails = useMemo(() => {
		if (!slatesData || !notificationsListOpenMarket) return [];

		const notifiedMatchIds = new Set(
			notificationsListOpenMarket.map((notification: any) => notification.match_id)
		);

		return slatesData.flatMap((slate) =>
			(slate.matches || [])
				.filter(
					(match: any) => !!match?.opening_description?.trim() && !notifiedMatchIds.has(match.id)
				)
				.map((match: any) => ({
					id: match.id,
					name: match.name,
					opening_description: match.opening_description.trim(),
				}))
		);
	}, [slatesData, notificationsListOpenMarket]);

	const handleSubscribeToOpenMarket = async (match_id: string) => {
		try {
			await subscribeToOpenMarket(match_id).unwrap();

			toast({
				title: 'Success',
				description: 'You have successfully subscribed to open market updates!',
			});
		} catch (error) {
			console.error('Subscription error:', error);

			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Failed to subscribe to open market. Check the console for details.',
			});

			throw new Error('Unexpected error during subscription process');
		}
	};

	return { matchOpeningDetails, handleSubscribeToOpenMarket };
}
